import { useEffect, useState } from 'react'
import classes from './Assessment.module.scss'
import RequiredAssessment from './RequiredAssessment'
import OtherAssessment from './OtherAssessment'
import Icon from '../../../Icon'
import Button from '../../shared/ComponentLibrary/RButton'
import PageHeading from '../../shared/PageHeading'
import { useSelector } from 'react-redux'
import BreadcrumbsPath from '../../shared/BreadcrumbsPath'
import { Modal } from 'react-bootstrap'
import useAuthentication from '../../../customHooks/useAuthentication'

import { useCrossPageAssessmentMethods } from '../useCrossPageAssessmentMethods'


const AssessmentIntroduction = () => {
  const profileStatus: UserProfileStatus = useSelector((state: ProfileState) => state.profile)
  const [profileStatusData, setProfileStatusData] = useState<UserProfileStatus>(profileStatus)
  const [profileData, setProfileData] = useState<UserProfile>()
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false)
  const { getUserInformation } = useAuthentication()

  const { getRequiredAssessments, getOtherAssessments } = useCrossPageAssessmentMethods()

  useEffect(() => getUserInformation(setProfileData), [])

  return (
    <div>
      <BreadcrumbsPath />
      <div className={classes.help_button_position}>
        <button type='button' className={classes.help_button} aria-label="More Information" title="More Information" onClick={() => setShowHelpModal(true)}>
          <div>More information</div>
          <div className={classes.help_button_icon}>
            <Icon name='hollowCircle' size='30' />
            <div>i</div>
          </div>
        </button>
      </div>
      <div className={classes.assessment_container}>

        <PageHeading
          icon='testResults'
          title='Assessment'
          divider
        />


        <p className={classes.base_level_text}>
          The final step for document review assignment is to take one or more assessments, depending on the proficiencies you have mentioned in your profile.
        </p>

        <h4 className={classes.base_level_text}>Required Assessment{getRequiredAssessments().length !== 1 ? 's' : ''}</h4>
        {[...getRequiredAssessments(), ...getOtherAssessments()].length > 1 &&
          <p className={classes.base_level_text}>
            {
              profileData && (profileData.attorney ? 'Attorneys should take the Document Review Skills assessment first.' : 'Paralegals and JDs should take the Cyber Skills assessment first.')
            }
          </p>
        }

        {!profileStatusData.profileStatus &&
          <p className={classes.base_level_text}>
            <strong>You must complete your profile before taking the assessment{ [...getRequiredAssessments(), ...getOtherAssessments()].length > 1 ? 's' : '' }.</strong>
          </p>
        }

        {
          getRequiredAssessments().map(assessment => <RequiredAssessment assessmentProps={assessment} enabled={profileStatusData.profileStatus} />)
        }

        {getOtherAssessments().some(x => true) && <>

          <h4 className={classes.other_assessment_h4}>Other Assessment{getOtherAssessments().length !== 1 ? 's' : ''}</h4>
          {
            getOtherAssessments().map(assessment => <OtherAssessment assessmentProps={assessment} enabled={profileStatusData.profileStatus} />)
          }
        </>}

        <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} centered size="xl" fullscreen="lg-down" dialogClassName={classes.help_modal} scrollable={true}>
          <Modal.Header>
            <Modal.Title>
              More Information &mdash; ReviewRight Document Review Assessments
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className={classes.base_level_text}>To qualify for document review projects, you must complete one or more assessments that match the proficiencies listed in your profile.</p>
            <hr />
            <h4>Required Assessments</h4>
            <p className={classes.base_level_text}>This section outlines the assessments needed to become eligible for review assignments.</p>
            <ul className={classes.base_level_text}>
                <li><strong>Attorneys</strong> should begin with the <strong>Document Review Skills Assessment</strong>.</li>
                <li><strong>Paralegals and JDs</strong> should begin with the <strong>Cyber Skills Assessment</strong>.</li>
            </ul>
            <h5>Assessment Availability and Retake Policy</h5>
            <ul className={classes.base_level_text}>
                <li>You can complete each required assessment <strong>once</strong>, as long as you <strong>pass</strong>.</li>
                <li>If you <strong>fail</strong> an assessment, you can retake it <strong>every 3 months</strong>.</li>
                <li>If you accumulate <strong>three invalid attempts</strong> (e.g., due to technical issues, incomplete submissions, or policy violations), you will be eligible to retake the assessment again <strong>after 3 months</strong>.</li>
                <li>After <strong>three months have passed</strong> and you are ready to take the test again, please contact <strong>staffing@haystackid.com</strong> for assistance.</li>
            </ul>
            <p className={classes.base_level_text}>If you have multiple assessments listed, you're eligible to complete <strong>each one separately</strong>, following the same pass/fail and retake timelines.</p>
            <h5>Preparation</h5>
            <p className={classes.base_level_text}>Each required assessment includes detailed instructions — please review them thoroughly before beginning.</p>
            <ul className={classes.base_level_text}>
                <li>For the <strong>Document Review Skills Assessment</strong>, a <strong>practice test</strong> is available to help you prepare.</li>
            </ul>
            <h5>Getting Started</h5>
            <p className={classes.base_level_text}>To begin:</p>
            <ol className={classes.base_level_text}>
                <li>Complete your profile (this is required before starting any assessment).</li>
                <li>Choose your assessment language.</li>
                <li>Click <strong>“Start Assessment”</strong> when you're ready.</li>
            </ol>
            <p className={`${classes.base_level_text} ${classes.indent_note}`}>Note: You can access instructions and practice tests even before your profile is complete.</p>
            <h5>Language Requirements</h5>
            <ul className={classes.base_level_text}>
                <li>If your profile includes multiple languages, you may be required to complete assessments in <strong>each listed language</strong> to be eligible for projects in those languages.</li>
                <li>If you do not complete the required assessments in each language, <strong>ReviewRight cannot guarantee project consideration</strong> in that language.</li>
                <li>Once you’ve completed required assessments, you may opt to take them in <strong>additional languages</strong> by selecting from the dropdown menu.</li>
            </ul>
            <hr />
            <h4>Other Assessments</h4>
            <p className={classes.base_level_text}>You may also see a section labeled <strong>“Other Assessments.”</strong></p>
            <ul className={classes.base_level_text}>
                <li>These are <strong>optional</strong>, unless you are specifically asked to complete one.</li>
                <li>New assessments may be added periodically. If you’ve worked with Haystack before and a new assessment is introduced for your listed proficiencies, it will be <strong>optional but recommended</strong>.</li>
            </ul>
          </Modal.Body>
          <Modal.Footer className='d-flex justify-content-evenly pt-4'>
            <Button innerText='OK' onClickMethod={() => setShowHelpModal(false)} />
          </Modal.Footer>
        </Modal>
      </div>

    </div>
  )
}

export default AssessmentIntroduction
